import { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth } from '../../../../firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { deriveTimerState, TIMER_STATE } from '../timer/lessonTimerModel';

/**
 * The lesson timer — 1s tick over the pure derivation in lessonTimerModel.
 *
 * Writes only the fields the old page already wrote ({startedAt,
 * status:'in-progress'} / {completedAt, status:'completed'}); every state is
 * DERIVED and nothing new persists — the countdown needed no schema change,
 * because `lesson.date` + `lesson.time` + `lesson.duration` were already there.
 * Verified sole-consumer of lesson.status='in-progress' before this was built.
 *
 * SINCE 2026-09-02 THE CLOCK NO LONGER DEPENDS ON ANYONE PRESSING START (see
 * lessonTimerModel.js). `start()` now records the ACTUAL start — which is what
 * produces the running-behind figure — rather than beginning the count. Two
 * consequences worth holding:
 *  - the first jot still stamps a missing start, so a teacher who never taps
 *    the pill still leaves a true record of when the lesson really began;
 *  - **timer state is no longer evidence that a lesson happened.** Attendance
 *    is written ONLY by complete() and by the explicit outcome control. Nothing
 *    here may start inferring it from a clock that advances on its own.
 */
const useLessonTimer = ({ lessonId, lesson, setLesson }) => {
	const [nowMs, setNowMs] = useState(() => Date.now());
	const [writeError, setWriteError] = useState('');
	// Guards the auto-start against double-fire (first jot + a tap racing).
	const startingRef = useRef(false);

	useEffect(() => {
		const tick = setInterval(() => setNowMs(Date.now()), 1000);
		return () => clearInterval(tick);
	}, []);

	// Read (never written) so a past lesson's pill can name its attendance.
	// Shorthand on purpose: the attendance pin counts the literal key.
	const outcome = lesson?.outcome;
	const timer = deriveTimerState({
		startedAt: lesson?.startedAt,
		completedAt: lesson?.completedAt,
		status: lesson?.status,
		outcome,
		durationMinutes: lesson?.duration,
		scheduledDate: lesson?.date,
		scheduledTime: lesson?.time,
		nowMs,
	});

	const start = useCallback(async () => {
		if (!lessonId || startingRef.current) return;
		startingRef.current = true;
		setWriteError('');
		const startedAt = Timestamp.now();
		try {
			await updateDoc(doc(db, 'lessons', lessonId), { startedAt, status: 'in-progress' });
			setLesson((prev) => (prev ? { ...prev, startedAt, status: 'in-progress' } : prev));
		} catch (error) {
			console.error('Error starting lesson timer:', error);
			setWriteError('Timer did not start. Tap the pill to try again.');
		} finally {
			startingRef.current = false;
		}
	}, [lessonId, setLesson]);

	/**
	 * First-jot auto-start: stamp the actual start if nothing has stamped it yet.
	 *
	 * Keys on `startedAt` being ABSENT rather than on the pill reading IDLE. Under
	 * the countdown, IDLE only occurs for a lesson with no usable schedule, so the
	 * old check would have quietly stopped firing for every normal lesson — the
	 * exact record this exists to capture. Completed lessons are excluded: a jot
	 * after wrap-up must not rewrite when the lesson began.
	 *
	 * Past lessons are excluded too (2026-09-29): a note typed into a lesson
	 * opened the next day stamped "started now" on it, and two September
	 * lessons carry a start a day after they were taught.
	 */
	const isPast = timer.state === TIMER_STATE.PAST;
	const startIfIdle = useCallback(() => {
		if (isPast || lesson?.startedAt || lesson?.completedAt || lesson?.status === 'completed') return;
		start();
	}, [isPast, lesson?.startedAt, lesson?.completedAt, lesson?.status, start]);

	const clearWriteError = useCallback(() => setWriteError(''), []);

	/** Completing stops the timer (wrap-up's Complete calls this). */
	const complete = useCallback(async () => {
		if (!lessonId) return false;
		setWriteError('');
		const completedAt = Timestamp.now();
		// Completing a lesson IS attendance: a teacher who taught it and wrote
		// the recap should not owe a second tap on Today. Only fills an EMPTY
		// outcome — a no_show or an admin's cancelled_cyv already on the record
		// is a stronger statement than "the dashboard was closed" and stands.
		// (Attendance arc, Phase A, card fwMubDlBzniA3JQtbUGi.)
		const attendance = lesson?.outcome
			? {}
			: { outcome: 'attended', outcomeAt: completedAt, outcomeBy: auth.currentUser?.uid || null };
		try {
			await updateDoc(doc(db, 'lessons', lessonId), { completedAt, status: 'completed', ...attendance });
			setLesson((prev) => (prev ? { ...prev, completedAt, status: 'completed', ...attendance } : prev));
			return true;
		} catch (error) {
			console.error('Error completing lesson:', error);
			setWriteError('Could not mark the lesson complete. Try again.');
			return false;
		}
	}, [lessonId, setLesson, lesson?.outcome]);

	return { timer, start, startIfIdle, complete, writeError, clearWriteError };
};

export default useLessonTimer;
